import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../utils/api';

const SSOConfiguration = () => {
  const [config, setConfig] = useState({
    entryPoint: '',
    issuer: `${window.location.origin}/auth/saml/metadata`, // Auto-populate with correct Entity ID
    idpIssuer: '',
    replyUrl: `${window.location.origin}/auth/saml/callback`, // Auto-populate Reply URL
    logoutUrl: '',
    certificate: '',
    adminGroups: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPassed, setTestPassed] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [enablingSSO, setEnablingSSO] = useState(false);
  const [ssoStatus, setSsoStatus] = useState({ enabled: false, configured: false });

  useEffect(() => {
    loadConfig();
    loadSSOStatus();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/sso/config');
      const loadedConfig = response.data.config;
      // Always ensure the Entity ID and Reply URL are set correctly, regardless of what's stored
      setConfig({
        ...loadedConfig,
        issuer: `${window.location.origin}/auth/saml/metadata`,
        replyUrl: loadedConfig.replyUrl || `${window.location.origin}/auth/saml/callback`
      });
    } catch (error) {
      console.error('SSO config error:', error);
      // Don't show error toast if it's just setup required
      if (!error.response?.data?.config?.setupRequired) {
        toast.error('Failed to load SSO configuration');
      }
      // Handle setup required in error case
      if (error.response?.data?.config?.setupRequired) {
        setConfig({
          entryPoint: '',
          issuer: `${window.location.origin}/auth/saml/metadata`,
          idpIssuer: '',
          replyUrl: `${window.location.origin}/auth/saml/callback`,
          logoutUrl: '',
          certificate: '',
          adminGroups: '',
          setupRequired: true,
          setupMessage: error.response.data.config.setupMessage
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSSOStatus = async () => {
    try {
      const response = await api.get('/sso/status');
      setSsoStatus(response.data);
      setSsoEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to load SSO status:', error);
    }
  };

  const handleToggleSSO = async () => {
    setEnablingSSO(true);
    try {
      const newEnabledState = !ssoEnabled;
      await api.put('/sso/enable', { enabled: newEnabledState });
      setSsoEnabled(newEnabledState);
      toast.success(`SSO ${newEnabledState ? 'enabled' : 'disabled'} successfully`);
      await loadSSOStatus(); // Reload status to confirm
    } catch (error) {
      const message = error.response?.data?.error || `Failed to ${!ssoEnabled ? 'enable' : 'disable'} SSO`;
      toast.error(message);
    } finally {
      setEnablingSSO(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/sso/config', config);
      toast.success('SSO configuration saved successfully');
      await loadSSOStatus(); // Reload status after saving
    } catch (error) {
      const message = error.response?.data?.details?.[0]?.msg || error.response?.data?.error || 'Failed to save SSO configuration';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetupDatabase = async () => {
    setSaving(true);
    try {
      const response = await api.post('/sso/setup');
      if (response.data.success) {
        toast.success(response.data.message);
        // Reload configuration after setup
        await loadConfig();
      } else {
        toast.error(response.data.error || 'Setup failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to setup SSO database');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestPassed(false);
    try {
      const response = await api.post('/sso/test', config);
      if (response.data.success) {
        setTestPassed(true);
        toast.success('SSO configuration test successful! You can now save the configuration.');
      } else {
        // Show detailed test results
        const testResults = response.data.testResults || {};
        const failedTests = Object.entries(testResults)
          .filter(([key, value]) => !value)
          .map(([key]) => {
            switch(key) {
              case 'entryPointValid': return 'Entry Point URL invalid';
              case 'issuerValid': return 'Issuer (Entity ID) missing';
              case 'certificateValid': return 'Certificate invalid or missing';
              case 'idpIssuerValid': return 'Azure AD Identifier invalid or missing';
              case 'adminGroupsConfigured': return 'Admin Groups not configured (optional)';
              default: return key;
            }
          });
        
        const errorMessage = `SSO configuration issues: ${failedTests.join(', ')}`;
        toast.error(errorMessage);
        console.error('SSO Test Results:', testResults);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'SSO configuration test failed';
      toast.error(errorMessage);
      console.error('SSO Test Error:', error);
    } finally {
      setTesting(false);
    }
  };

  const handleTestFlow = async () => {
    setTesting(true);
    setTestPassed(false);
    
    try {
      // Create test session with unsaved configuration
      const response = await api.post('/sso/test-config', {
        entryPoint: config.entryPoint,
        certificate: config.certificate,
        issuer: config.issuer,
        idpIssuer: config.idpIssuer
      });
      
      if (!response.data.success) {
        toast.error(response.data.error || 'Failed to create test session');
        setTesting(false);
        return;
      }

      const { testUrl } = response.data;
      
      // Open Microsoft login in a popup
      const popup = window.open(
        testUrl,
        'sso-test',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        toast.error('Popup blocked! Please allow popups for this site and try again.');
        setTesting(false);
        return;
      }

      toast('Please log in with YOUR credentials in the popup window (you may need to select "Use another account" if it shows a different user)...', {
        icon: 'ℹ️',
        duration: 6000
      });

      // Listen for test results from popup
      const handleMessage = (event) => {
        if (event.data.type === 'SSO_TEST_RESULT') {
          if (event.data.success) {
            setTestPassed(true);
            toast.success(
              `✅ SSO Test Successful! Logged in as ${event.data.profile.email}. You can now save your configuration.`,
              { duration: 6000 }
            );
            console.log('SSO Test Profile:', event.data.profile);
          } else {
            setTestPassed(false);
            toast.error(`SSO Test Failed: ${event.data.error}`, { duration: 8000 });
          }
          setTesting(false);
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was closed before completing
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          if (testing) {
            setTesting(false);
            if (!testPassed) {
              toast('Test window closed. Complete the login to validate your configuration.', {
                icon: 'ℹ️',
              });
            }
          }
        }
      }, 1000);

    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to start SSO test';
      toast.error(errorMessage);
      console.error('SSO Test Flow Error:', error);
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">SSO Configuration</h3>
          <p className="mt-1 text-sm text-gray-600">
            Configure SAML2 Single Sign-On with Microsoft Entra ID
          </p>
        </div>
        
        {/* SSO Enable/Disable Toggle */}
        {ssoStatus.configured && (
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${ssoEnabled ? 'text-green-700' : 'text-gray-500'}`}>
              {ssoEnabled ? '✅ SSO Enabled' : 'SSO Disabled'}
            </span>
            <button
              onClick={handleToggleSSO}
              disabled={enablingSSO || !ssoStatus.configured}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                ssoEnabled ? 'bg-green-600' : 'bg-gray-200'
              } ${enablingSSO ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={ssoStatus.configured ? (ssoEnabled ? 'Disable SSO' : 'Enable SSO') : 'Configure SSO first'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  ssoEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}
      </div>
      
      {/* Status Banner */}
      {ssoStatus.configured && !ssoEnabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">SSO is Configured but Disabled</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Your SSO configuration is complete but not active. Enable it using the toggle above to allow users to log in with Microsoft Entra ID.</p>
              </div>
            </div>
          </div>
        </div>
      )}



      {config.setupRequired && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">Database Setup Required</h4>
          <p className="text-sm text-yellow-800 mb-3">
            {config.setupMessage || 'The SSO configuration tables need to be created in the database.'}
          </p>
          <button
            onClick={handleSetupDatabase}
            disabled={saving}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Setting up...' : 'Setup SSO Database'}
          </button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-6">

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Entry Point URL
            </label>
            <input
              type="url"
              value={config.entryPoint}
              onChange={(e) => {
                setConfig({...config, entryPoint: e.target.value});
                setTestPassed(false);
              }}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="https://login.microsoftonline.com/your-tenant-id/saml2"
            />
            <p className="mt-1 text-xs text-gray-500">
              The SAML SSO URL from your Entra ID Enterprise Application
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Issuer (Entity ID)
            </label>
            <input
              type="text"
              value={config.issuer}
              readOnly
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
              placeholder="https://your-domain.com/auth/saml/metadata"
            />
            <p className="mt-1 text-xs text-gray-500">
              The unique identifier for this application (Service Provider) - Auto-generated
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Azure AD Identifier (IdP Issuer)
            </label>
            <input
              type="url"
              value={config.idpIssuer}
              onChange={(e) => {
                setConfig({...config, idpIssuer: e.target.value});
                setTestPassed(false);
              }}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="https://sts.windows.net/your-tenant-id/"
            />
            <p className="mt-1 text-xs text-gray-500">
              The Azure AD Identifier from your Entra ID Enterprise Application
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Reply URL (Assertion Consumer Service)
            </label>
            <input
              type="url"
              value={config.replyUrl}
              readOnly
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
              placeholder="https://your-domain.com/auth/saml/callback"
            />
            <p className="mt-1 text-xs text-gray-500">
              Where Azure sends SAML responses - Use this exact URL in Azure configuration
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Logout URL (Optional)
            </label>
            <input
              type="url"
              value={config.logoutUrl}
              onChange={(e) => {
                setConfig({...config, logoutUrl: e.target.value});
              }}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="https://login.microsoftonline.com/your-tenant-id/saml2/logout"
            />
            <p className="mt-1 text-xs text-gray-500">
              Azure logout URL for Single Logout (SLO) - Optional but recommended
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              X.509 Certificate
            </label>
            <textarea
              value={config.certificate}
              onChange={(e) => {
                setConfig({...config, certificate: e.target.value});
                setTestPassed(false);
              }}
              rows={8}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono text-xs"
              placeholder="-----BEGIN CERTIFICATE-----&#10;MIICXjCCAcegAwIBAgIBADANBgkqhkiG9w0BAQ0FADBLMQswCQYDVQQGEwJ1czEL&#10;...&#10;-----END CERTIFICATE-----"
            />
            <p className="mt-1 text-xs text-gray-500">
              The X.509 certificate from your Entra ID Enterprise Application (without private key)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Admin Groups (comma-separated)
            </label>
            <input
              type="text"
              value={config.adminGroups}
              onChange={(e) => {
                setConfig({...config, adminGroups: e.target.value});
                setTestPassed(false);
              }}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="CursorAnalytics-Admins,IT-Administrators"
            />
            <p className="mt-1 text-xs text-gray-500">
              Entra ID groups that should be granted admin access
            </p>
          </div>

          <div className="flex flex-col space-y-3">
            <div className="flex space-x-3">
              <button
                onClick={handleTest}
                disabled={testing || !config.entryPoint || !config.issuer || !config.idpIssuer || !config.certificate}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? 'Testing...' : 'Validate Configuration'}
              </button>
              
              <button
                onClick={handleTestFlow}
                disabled={testing || !config.entryPoint || !config.issuer || !config.idpIssuer || !config.certificate}
                className="inline-flex justify-center py-2 px-4 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? 'Opening Test...' : '🔐 Test SSO Login'}
              </button>
            </div>
            
            <button
              onClick={handleSave}
              disabled={saving || !testPassed || !config.entryPoint || !config.issuer || !config.idpIssuer || !config.certificate}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : testPassed ? 'Save Configuration' : 'Test Required Before Save'}
            </button>
          </div>
          
          {testPassed && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    Configuration test passed! You can now save the settings.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Setup Instructions:</h4>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Create an Enterprise Application in Microsoft Entra ID</li>
          <li>Set the Identifier (Entity ID) to the Issuer value above</li>
          <li>Set the Reply URL to: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/auth/saml/callback</code></li>
          <li>Copy the Login URL and Certificate from Entra ID to the fields above</li>
          <li>Configure user assignments and group claims in Entra ID</li>
          <li>Test the configuration, then enable SSO</li>
        </ol>
      </div>
    </div>
  );
};

export default SSOConfiguration;
